import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { DocumentStatusId } from '@/modules/common/enums/document-status-id.enum.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import DocumentTypeOption from '@/modules/common/models/option/document-type-option.ts';
import ProjectOption from '@/modules/common/models/option/project-option.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { EntityLockApi } from '@/modules/core/api/entity-lock.api.ts';
import { SystemApi } from '@/modules/core/api/system.api.ts';
import type { EntityLock } from '@/modules/core/types/entity-lock.type.ts';
import { CONTROL_TYPE_AFTER } from '@/modules/document/composables/use-launch-options.ts';
import { ENTITY_LOCK_EXPIRATION_SECONDS, EntityLockKeeper } from '@/modules/core/utils/entity-lock-keeper.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import { useRolesStore } from '@/modules/core/stores/roles.store.ts';
import { Role } from '@/modules/common/enums/role.ts';
import type { ColorCode } from '@/modules/core/enums/color-code.enum.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { DocumentApi } from '@/modules/document/api/document.api.ts';
import DocumentLegacyApi from '@/modules/document/api/document-api.ts';
import DocumentMasterApi from '@/modules/document/api/document-master-api.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { useDocGanttStore } from '@/modules/document/stores/doc-gantt.store.ts';
import { useDocumentRowStore } from '@/modules/document/stores/document-row.store.ts';
import type { Mask } from '@/modules/document/document-master/types/mask.ts';
import type { Parameter } from '@/modules/document/models/parameter.ts';
import {
  carryOverMaskValues,
  createContentSource,
  findSameListOption,
} from '@/modules/document/document-edit/utils/mask-carry-over.ts';
import type { DocumentInternalDto } from '@/modules/document/dto/document-internal.dto.ts';
import type {
  DocumentEditBlock,
  DocumentEditFormData,
  DocumentEditFormState,
  DocumentLaunchMode,
} from '@/modules/document/document-edit/types/document-edit-form.type.ts';
import {
  createDeadline,
  createLaunchDeadline,
  createDelayedLaunch,
  createRegistration,
  type EditSavePlan,
  getLaunchMode,
  isRemovedFromControl,
  parseMaskFormData,
  planSave,
} from '@/modules/document/document-edit/utils/edit-snapshot.ts';
import {
  EMPTY_COLOR_SOURCES,
  findSourceColorLock,
  type SourceColorLock,
} from '@/modules/document/document-edit/utils/color-lock.ts';
import type { DocumentColorSources } from '@/modules/document/types/document-color.type.ts';
import { publishDocumentUpdated } from '@/modules/shared/composables/document-update-sync.ts';

type SessionState = {
  documentId: number | null;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isMaskLoading: boolean;
  validity: Partial<Record<DocumentEditBlock, boolean>>;
  showErrors: boolean;
  maskCache: Record<number, DocumentEditFormState['mask']>;
};

type State = SessionState & {
  snapshot: DocumentInternalDto | null;
  form: DocumentEditFormData | null;
  controllers: Option<number>[];
  systemController: Option<number> | null;
  initialSourceColorLock: SourceColorLock | null;
  initialSourceColor: ColorCode | null;
  colorSources: DocumentColorSources;
  systemUserId: number | null;
  returnControllerId: number | null;
  documentStatus: DocumentStatusId | null;
};

const createSessionState = (): SessionState => ({
  documentId: null,
  isEditing: false,
  isLoading: false,
  isSaving: false,
  isMaskLoading: false,
  validity: {},
  showErrors: false,
  maskCache: {},
});

const createState = (): State => ({
  ...createSessionState(),
  snapshot: null,
  form: null,
  controllers: [],
  systemController: null,
  initialSourceColorLock: null,
  initialSourceColor: null,
  colorSources: EMPTY_COLOR_SOURCES,
  systemUserId: null,
  returnControllerId: null,
  documentStatus: null,
});

const createEmptyMask = (): Mask => ({ parameters: [], formData: {} });

const lockKeeper = new EntityLockKeeper();

let generation = 0;

function nextGeneration() {
  const current = ++generation;

  return () => current === generation;
}

function isLockedByOther(lock: EntityLock) {
  return lock.isLocked && lock.userName !== useUserStore().name;
}

function notifyLocked({ userName }: EntityLock) {
  notification.warning({ key: 'document.edit.notification.locked', params: { user: userName ?? '' } });
}

let lastRelease: Promise<void> = Promise.resolve();

function releaseLock(documentId: number) {
  lastRelease = EntityLockApi.unlock(TableId.Documents, documentId).catch(() => undefined);

  return lastRelease;
}

async function loadMask(
  type: Pick<DocumentTypeOption, 'code' | 'mask'>,
  content: string,
  contentMask: string | null
): Promise<Mask> {
  if (!type.mask) {
    return createEmptyMask();
  }

  const [parameters, masks] = await Promise.all([
    DocumentMasterApi.getParameters(type.code),
    contentMask ? DocumentLegacyApi.getMasks([contentMask]) : [],
  ]);

  return { parameters, formData: parseMaskFormData(parameters, content, masks[0]) };
}

async function findListOption(typeId: number, parameter: Pick<Parameter, 'number'>, value: Option) {
  const options = await DocumentMasterApi.getParameterOptions(typeId, parameter.number);

  return findSameListOption(options, value);
}

async function findReturnController(documentId: number, controllers: Option<number>[]) {
  try {
    const name = await DocumentApi.getPreviousControllerName(documentId);
    const matches = controllers.filter(({ label }) => label === name);

    return matches.length === 1 ? matches[0] : null;
  } catch {
    return null;
  }
}

function reportError(key: string, error: unknown) {
  if (ErrorUtil.isBusinessError(error) || ErrorUtil.isUnexpected(error)) {
    notification.error(key, error);
  }
}

async function attempt(key: string, action: () => Promise<unknown>) {
  try {
    await action();
    return true;
  } catch (error) {
    reportError(key, error);
    return false;
  }
}

type LoadedSession = Omit<State, keyof SessionState> & {
  snapshot: DocumentInternalDto;
  form: DocumentEditFormData;
  systemUserId: number;
};

function loadRow(documentId: number) {
  const rowStore = useDocumentRowStore();

  return rowStore.item['DOC_ID'] === documentId ? rowStore.item : rowStore.fetchOnly(documentId);
}

async function loadSession(documentId: number): Promise<LoadedSession> {
  const [row, snapshot, types, projects, controllers, color, colorSources, systemUserId] = await Promise.all([
    loadRow(documentId),
    DocumentApi.getInternal(documentId),
    DocumentMasterApi.getTypes(),
    DocumentMasterApi.getProjects(),
    DocumentApi.getAvailableControllers(documentId),
    DocumentApi.getOwnColorState(documentId),
    DocumentApi.getColorSources().catch(() => EMPTY_COLOR_SOURCES),
    SystemApi.getUserId(),
  ]);

  const type =
    types.find(({ code }) => code === snapshot.DT_ID) ??
    DocumentTypeOption.createByResponse({
      DT_ID: snapshot.DT_ID,
      Название: row['Тип'],
      Маска: row['Содержание_MASK'],
      Активен: 1,
    });
  const project =
    projects.find(({ code }) => code === snapshot.P_ID) ??
    new ProjectOption({ P_ID: snapshot.P_ID, Название: row['Проект'], Активен: 1 });
  const allControllerOptions = controllers.map(({ LOGIN_ID, ФИО }) => ({ code: LOGIN_ID, label: ФИО }));
  const systemController = allControllerOptions.find(({ code }) => code === systemUserId) ?? null;
  const regularControllerOptions = allControllerOptions.filter(({ code }) => code !== systemUserId);
  const removedFromControl = isRemovedFromControl(snapshot, systemUserId);
  const [returnController, mask] = await Promise.all([
    removedFromControl ? findReturnController(documentId, regularControllerOptions) : null,
    loadMask(type, snapshot.Содержание, (row['Содержание_MASK'] as string | null) ?? null),
  ]);

  const controller = allControllerOptions.find(({ code }) => code === snapshot.CONTROLLER_ID) ?? {
    code: snapshot.CONTROLLER_ID,
    label: String(row['Контролер'] ?? ''),
  };

  return {
    snapshot,
    controllers: regularControllerOptions,
    systemController,
    initialSourceColorLock: color.isLockedBySource ? { color: color.sourceColor, source: color.lockSource } : null,
    initialSourceColor: color.sourceColor,
    colorSources,
    systemUserId,
    returnControllerId: returnController?.code ?? null,
    documentStatus: (row['DS_ID'] as DocumentStatusId | undefined) ?? null,
    form: {
      type,
      project,
      mask,
      content: snapshot.Содержание,
      registration: createRegistration(snapshot),
      controller,
      deadline: createDeadline(snapshot.Срок),
      removedFromControl,
      launchControlType: CONTROL_TYPE_AFTER,
      launch: createLaunchDeadline(snapshot.Запуск),
      ...createDelayedLaunch(snapshot, systemUserId),
      color: color.color,
      colorLocked: color.isLocked,
      comment: snapshot.Комментарий ?? '',
    },
  };
}

async function applySavePlan(documentId: number, plan: EditSavePlan, systemUserId: number | null) {
  const { registration, removeFromControl, launchDate, pause } = plan;
  const results: boolean[] = [];

  if (registration) {
    results.push(
      await attempt('document.edit.notification.registrationError', () =>
        DocumentApi.setRegistrationData(documentId, registration.number, registration.date)
      )
    );
  }

  if (launchDate) {
    results.push(
      await attempt('document.edit.notification.launchError', () => DocumentApi.setStartDate(documentId, launchDate))
    );
  }

  if (pause) {
    results.push(await attempt('document.edit.notification.launchError', () => DocumentApi.pause(documentId)));
  }

  if (removeFromControl && systemUserId) {
    results.push(
      await attempt('document.edit.notification.controlError', () =>
        DocumentApi.setController(documentId, systemUserId)
      )
    );
  }

  return results.every(Boolean);
}

export const useDocumentEditStore = defineStore(StoreId.DocumentEdit, {
  state: createState,
  getters: {
    isValid: (state) => Object.values(state.validity).every(Boolean),
    isEditingDocument: (state) => (documentId: number) => state.isEditing && state.documentId === documentId,
    launchMode: (state): DocumentLaunchMode => getLaunchMode(state.documentStatus),
    controllerOptions: ({ controllers, form, systemController, systemUserId }) =>
      form?.controller?.code === systemUserId && systemController ? [...controllers, systemController] : controllers,
    warnsAboutEditLoss: () => !useRolesStore().has(Role.Administrator),
    displayedColor(): ColorCode | null {
      const { form, snapshot, initialSourceColor, colorSources, sourceColorLock } = this;
      if (!form || !snapshot) {
        return null;
      }

      if (sourceColorLock) {
        return sourceColorLock.color;
      }

      if (form.color !== null || form.colorLocked) {
        return form.color;
      }

      const projectId = form.project?.code ?? null;
      if (form.type.code === snapshot.DT_ID && projectId === snapshot.P_ID) {
        return initialSourceColor;
      }

      return (
        colorSources.types[form.type.code]?.color ??
        (projectId !== null ? colorSources.projects[projectId]?.color : null) ??
        null
      );
    },
    sourceColorLock: ({ form, snapshot, initialSourceColorLock, colorSources }): SourceColorLock | null => {
      if (!form || !snapshot) {
        return null;
      }

      const projectId = form.project?.code ?? null;

      if (form.type.code === snapshot.DT_ID && projectId === snapshot.P_ID) {
        return initialSourceColorLock;
      }

      return findSourceColorLock(colorSources, form.type.code, projectId);
    },
  },
  actions: {
    async open(documentId: number) {
      if (this.isEditing || this.isLoading) {
        return;
      }

      const isCurrent = nextGeneration();
      let isLocked = false;

      this.isLoading = true;

      try {
        await lastRelease;

        if (!isCurrent()) {
          return;
        }

        const lock = await EntityLockApi.get(TableId.Documents, documentId);

        if (!isCurrent()) {
          return;
        }

        if (isLockedByOther(lock)) {
          notifyLocked(lock);
          return;
        }

        await EntityLockApi.lock(TableId.Documents, documentId, ENTITY_LOCK_EXPIRATION_SECONDS);
        isLocked = true;

        const [session, isEditedInOtherTab] = await Promise.all([
          loadSession(documentId),
          lock.isLocked && EntityLockKeeper.isHeldInOtherTab(TableId.Documents, documentId),
        ]);

        if (!isCurrent()) {
          void releaseLock(documentId);
          return;
        }

        this.$patch((state) => {
          Object.assign(state, { ...session, documentId, isEditing: true });
        });

        if (isEditedInOtherTab) {
          notification.warning('document.edit.notification.editingElsewhere');
        }

        lockKeeper.start(TableId.Documents, documentId, {
          isLockedByOther,
          onLost: (lock) => {
            notification.warning({
              key: 'document.edit.notification.lockLost',
              params: { user: lock.userName ?? '' },
            });
            void this.close({ releaseLock: false });
          },
          onRenewError: () => notification.warning('document.edit.notification.lockRenewError'),
        });
      } catch (error) {
        if (isLocked) {
          void releaseLock(documentId);
        }

        if (isCurrent()) {
          this.$reset();
          reportError('document.edit.notification.openError', error);
        }
      } finally {
        if (isCurrent()) {
          this.isLoading = false;
        }
      }
    },

    patchForm(patch: Partial<DocumentEditFormState>) {
      if (this.isEditing && this.form) {
        Object.assign(this.form, patch);
      }
    },

    setDelayedLaunch(delayedLaunch: boolean) {
      if (!delayedLaunch && this.form?.stopControlBeforeLaunch) {
        this.setStopControlBeforeLaunch(false);
      }

      this.patchForm({ delayedLaunch });
    },

    setStopControlBeforeLaunch(stopControlBeforeLaunch: boolean) {
      if (!this.form) {
        return;
      }

      if (stopControlBeforeLaunch) {
        if (this.form.controller?.code !== this.systemUserId) {
          this.returnControllerId = this.form.controller?.code ?? this.returnControllerId;
        }

        this.patchForm({
          stopControlBeforeLaunch,
          controller: this.systemController ?? this.form.controller,
        });
        return;
      }

      const returnController = this.controllers.find(({ code }) => code === this.returnControllerId);

      this.patchForm({
        stopControlBeforeLaunch,
        controller:
          this.form.controller?.code === this.systemUserId ? (returnController ?? null) : this.form.controller,
      });
    },

    async setType(type: DocumentEditFormState['type']) {
      if (!this.isEditing || !this.form || !this.snapshot || this.form.type.code === type.code) {
        return;
      }

      const isCurrent = nextGeneration();
      const previous = this.form.mask;

      if (previous.parameters.length) {
        this.maskCache = { ...this.maskCache, [this.form.type.code]: previous };
      }

      this.patchForm({
        type,
        mask: createEmptyMask(),
        registration: this.form.registration.auto ? createRegistration(this.snapshot) : this.form.registration,
      });
      this.isMaskLoading = true;

      try {
        const { parameters } = await loadMask(type, '', null);
        const cached = this.maskCache[type.code];
        const sources = [
          previous.parameters.length ? previous : createContentSource(this.form.content),
          ...Object.values(this.maskCache).filter((mask) => mask !== previous),
        ];
        const formData = await carryOverMaskValues(parameters, cached?.formData ?? {}, sources, (parameter, value) =>
          findListOption(type.code, parameter, value)
        );

        if (isCurrent()) {
          this.patchForm({ mask: { parameters, formData } });
        }
      } catch (error) {
        if (isCurrent()) {
          reportError('document.edit.notification.maskError', error);
        }
      } finally {
        if (isCurrent()) {
          this.isMaskLoading = false;
        }
      }
    },

    setAutoRegistration(auto: boolean) {
      if (!this.form || !this.snapshot) {
        return;
      }

      this.patchForm({
        registration: auto
          ? { number: this.form.type.template ?? '', date: DateTime.now(), auto }
          : createRegistration(this.snapshot),
      });
    },

    setValidity(block: DocumentEditBlock, isValid: boolean | null) {
      if (isValid === null) {
        const { [block]: _removed, ...rest } = this.validity;
        this.validity = rest;
        return;
      }

      this.validity = { ...this.validity, [block]: isValid };
    },

    async save() {
      if (!this.isEditing || !this.form || !this.snapshot || this.isSaving) {
        return false;
      }

      if (this.isMaskLoading || !this.isValid) {
        this.showErrors = true;
        return false;
      }

      const { snapshot, systemUserId, returnControllerId, launchMode } = this;
      const form = this.sourceColorLock ? { ...this.form, color: null, colorLocked: false } : this.form;
      const documentId = snapshot.DOC_ID;
      let plan: EditSavePlan;

      this.isSaving = true;

      try {
        const lock = await EntityLockApi.get(TableId.Documents, documentId);

        if (isLockedByOther(lock)) {
          notifyLocked(lock);
          this.isSaving = false;
          return false;
        }

        plan = planSave(form, snapshot, systemUserId, returnControllerId, launchMode);
        await DocumentApi.edit(plan.params);
      } catch (error) {
        reportError('document.edit.notification.saveError', error);
        this.isSaving = false;
        return false;
      }

      const isApplied = await applySavePlan(documentId, plan, systemUserId);

      if (isApplied) {
        notification.success('document.edit.notification.saved');
      } else {
        notification.warning('document.edit.notification.savedPartially');
      }

      await this.close();
      void usePreviewStore().refreshPartlyPreviewAction(DocumentEndPoint.GetList, documentId);
      void useDocGanttStore().fetch({ doc_id: documentId });
      publishDocumentUpdated(documentId);

      return true;
    },

    async close({ releaseLock: shouldReleaseLock = true }: { releaseLock?: boolean } = {}) {
      const { documentId } = this;

      nextGeneration();
      lockKeeper.stop();
      // Данные формы не сбрасываем: на них DocumentEditForm доигрывает leave-переход, open() их перезапишет.
      this.$patch((state) => {
        Object.assign(state, createSessionState());
      });

      if (documentId && shouldReleaseLock) {
        await releaseLock(documentId);
      }
    },
  },
});
